import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import sh from "bundles/common/utils/sh";

function formatHM(secs) {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

export default function ProjectDistribution({ range = "today", startDate, endDate, refreshKey, compact = false }) {
  const [rows, setRows] = useState([]);

  const fetchRows = useCallback(() => {
    if (range === "custom" && (!startDate || !endDate)) return;
    const params = { range };
    if (range === "custom") { params.start_date = startDate; params.end_date = endDate; }
    sh.get("/internal/time_reports/distribution", { params })
      .then((res) => setRows(Array.isArray(res.data) ? res.data : []));
  }, [range, startDate, endDate]);
  useEffect(() => { fetchRows(); }, [fetchRows, refreshKey]);

  const totals = useMemo(() => {
    const arr = [...rows]
      .sort((a, b) => (b.total_secs || 0) - (a.total_secs || 0))
      .map((p) => ({ ...p, color: p.project_color}));
    const total = arr.reduce((s, x) => s + (x.total_secs || 0), 0);
    return { arr, total };
  }, [rows]);

  const radius = compact ? 50 : 65;
  const stroke = compact ? 18 : 20;
  const C = 2 * Math.PI * radius;
  const size = (radius + stroke) * 2;
  const GAP_PX = 3;
  let offset = 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        borderRadius: "20px",
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 24px hsla(150, 30%, 12%, 0.06), 0 1px 3px hsla(150, 30%, 12%, 0.04)",
        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
        height: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3.5, gap: 1.5 }}>
        <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 22, color: "hsl(150 30% 12%)", letterSpacing: -0.2, minWidth: 0 }} noWrap>
          Project distribution
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
          <Box component="svg" width={size} height={size}>
            {totals.total > 0 && totals.arr.map((p) => {
              const frac = (p.total_secs || 0) / totals.total;
              const segLen = frac * C;
              const dash = Math.max(0, segLen - GAP_PX);
              const el = (
                <circle
                  key={p.project_id}
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={p.color}
                  strokeWidth={stroke}
                  strokeDasharray={`${dash} ${C - dash}`}
                  strokeDashoffset={-offset}
                  transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
              );
              offset += segLen;
              return el;
            })}
          </Box>
          <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", textAlign: "center" }}>
            <Box>
              <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: "hsl(150 10% 46%)", mb: 0.25 }}>
                Total
              </Typography>
              <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 20, color: "hsl(150 30% 12%)", fontVariantNumeric: "tabular-nums", letterSpacing: -0.3 }}>
                {formatHM(totals.total)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        component="ul"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          listStyle: "none",
          p: 0,
          m: 0,
        }}
      >
        {totals.arr.map((p) => {
          const pct = totals.total > 0
            ? Math.round(((p.total_secs || 0) / totals.total) * 100)
            : 0;
          return (
            <Box component="li" key={p.project_id} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: p.color, flexShrink: 0 }} />
              <Typography sx={{ flex: 1, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 14, color: "hsl(150 30% 12%)" }} noWrap>
                {p.project_name || "—"}
              </Typography>
              <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 13, color: "hsl(150 10% 46%)", fontVariantNumeric: "tabular-nums", minWidth: 56, textAlign: "right" }}>
                {formatHM(p.total_secs || 0)}
              </Typography>
              <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 15, color: "hsl(150 30% 12%)", width: 44, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {pct}%
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}


